import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material/material.module';
import { UserPlansComponent } from '../user-plans/user-plans.component';
import { UserPaymentsComponent } from '../user-payments/user-payments.component';
import { SummaryCardsComponent } from './summary-cards/summary-cards.component';
import { TitularPendingPlansComponent } from '../titular-pending-plans/titular-pending-plans.component';
import { PayerPendingPlansComponent } from '../external-payer/payer-pending-plans/payer-pending-plans.component';
import { PayerPlansComponent } from '../external-payer/payer-plans/payer-plans.component';
import { PayerInstallmentsComponent } from '../external-payer/payer-installments/payer-installments.component';
import { DashboardSummary } from './models/dashboard-summary.interface';
import { AccessPlan, Payment, UserRef } from '../../../core/generated';
import { PlanPaymentDataService } from './services/plan-payment-data.service';
import { ExternalPayerService } from '../external-payer/services/external-payer.service';
import { UserPreferencesCacheService } from '../../../shared/services/user-preferences-cache.service';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { fadeIn, slideInOut } from '../shared/animations';

/**
 * PlanPaymentDashboardComponent
 * Container principal com tabs e dashboard de resumo
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 11.1, 11.2
 */
@Component({
  selector: 'app-plan-payment-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    SummaryCardsComponent,
    UserPlansComponent,
    UserPaymentsComponent,
    TitularPendingPlansComponent,
    PayerPendingPlansComponent,
    PayerPlansComponent,
    PayerInstallmentsComponent
  ],
  templateUrl: './plan-payment-dashboard.component.html',
  styleUrls: ['./plan-payment-dashboard.component.scss'],
  animations: [fadeIn, slideInOut]
})
export class PlanPaymentDashboardComponent implements OnInit, OnDestroy {
  private dataService = inject(PlanPaymentDataService);
  private externalPayerService = inject(ExternalPayerService);
  private userPreferencesService = inject(UserPreferencesCacheService);
  private destroy$ = new Subject<void>();
  
  // Tab state management (Requirement 2.3)
  selectedTabIndex: number = 0;
  
  // Dashboard summary data
  dashboardSummary: DashboardSummary = {
    totalPlans: 0,
    activePlans: 0,
    monthlyTotal: 0,
    nextDueDate: null,
    overdueCount: 0,
    totalPaid: 0,
    totalPending: 0
  };
  
  // Plans and payments data
  plans: AccessPlan[] = [];
  payments: Payment[] = [];
  
  // Selected plan ID for filtering payments
  selectedPlanId: number | null = null;
  
  // User data
  user: UserRef | null = null;
  
  // Loading state
  isLoading = false;

  /** CPF do usuário para identificação como pagador externo via CNPJs que possui */
  get userCpf(): string {
    return this.user?.cpf ?? '';
  }

  /** Indica se as abas de pagador devem aparecer (carregado após busca inicial) */
  hasPendingPayerPlans = false;
  hasAcceptedPayerPlans = false;

  /** ABA 3: titular tem planos com pagador externo ainda não respondido */
  get hasTitularPendingPlans(): boolean {
    return this.plans.some(p => p.payerCnpj && p.payerAccepted == null);
  }

  async ngOnInit(): Promise<void> {
    await this.loadUser();
    
    if (this.user?.cpf) {
      await this.loadData(this.user.cpf);
      this.subscribeToDashboardSummary();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load user data
   */
  private async loadUser(): Promise<void> {
    try {
      this.user = await firstValueFrom(this.userPreferencesService.getUser());
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  }

  /**
   * Load plans and payments data
   * Requirement: 15.5
   */
  private async loadData(cpfCnpj: string): Promise<void> {
    this.isLoading = true;
    
    try {
      // Load plans and payments in parallel
      await Promise.all([
        firstValueFrom(this.dataService.loadUserPlans(cpfCnpj)),
        firstValueFrom(this.dataService.loadUserPayments(cpfCnpj))
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      this.isLoading = false;
    }

    // Verificar se o usuário possui planos como pagador (pending ou accepted)
    try {
      const [pending, accepted] = await Promise.all([
        firstValueFrom(this.externalPayerService.getPendingPlansByUserCpf(cpfCnpj)),
        firstValueFrom(this.externalPayerService.getAcceptedPlansByUserCpf(cpfCnpj))
      ]);
      this.hasPendingPayerPlans = pending.length > 0;
      this.hasAcceptedPayerPlans = accepted.length > 0;
    } catch {
      this.hasPendingPayerPlans = false;
      this.hasAcceptedPayerPlans = false;
    }
  }

  /**
   * Subscribe to dashboard summary updates
   * Requirements: 1.1, 1.2
   */
  private subscribeToDashboardSummary(): void {
    this.dataService.calculateDashboardSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe(summary => {
        this.dashboardSummary = summary;
      });
    
    // Subscribe to plans data
    this.dataService.plans$
      .pipe(takeUntil(this.destroy$))
      .subscribe(plans => {
        this.plans = plans;
      });
    
    // Subscribe to payments data
    this.dataService.payments$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payments => {
        this.payments = payments;
      });
  }

  /**
   * Handle plan selection from UserPlansComponent
   * Requirements: 13.2
   */
  onPlanSelected(plan: AccessPlan): void {
    if (plan.id) {
      this.selectedPlanId = plan.id;
      // Switch to payments tab
      this.selectedTabIndex = 1;
    }
  }

  /**
   * Handle payment selection from UserPaymentsComponent
   * Requirements: 13.3
   */
  onPaymentSelected(payment: Payment): void {
    // This will be used for navigation back to plan
    // Implementation will be completed when modal components are created
  }

  /**
   * Handle tab change event
   * Requirements: 2.2, 2.3
   */
  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    
    // Clear plan filter when switching away from payments tab
    if (index !== 1) {
      this.selectedPlanId = null;
    }
  }

  /**
   * Refresh data
   */
  async refreshData(): Promise<void> {
    if (this.user?.cpf) {
      this.dataService.clearCache();
      await this.loadData(this.user.cpf);
    }
  }
}
